import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, ChevronRight, X, Trash2, Loader2, Edit3, Clock,
  MapPin, User, ShieldAlert,
} from "lucide-react";
import { buildMonthGrid, formatFechaCorta, toLocalYmd, todayLocalYmd } from "@/lib/workOrders";
import LiveSearch, { matchesQuery } from "@/components/LiveSearch";

const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"]; // Mon=0..Sun=6

// Expand a reservation to the list of dates it covers, taking into account
// the optional weekday recurrence.
function _expandCraneDates(r) {
  const start = r.fecha;
  const end = r.fecha_fin || r.fecha;
  if (!start) return [];
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return [start];
  const recur = Array.isArray(r.recurrencia_dias) ? r.recurrencia_dias : [];
  const out = [];
  for (let i = 0, d = new Date(s); d <= e && i < 400; i++, d.setDate(d.getDate() + 1)) {
    const dow = (d.getDay() + 6) % 7; // Mon=0
    if (recur.length === 0 || recur.includes(dow)) {
      out.push(toLocalYmd(d));
    }
  }
  return out;
}

/**
 * Crane reservation calendar.
 *
 * Access model (enforced by backend):
 *   • sadmin: implicit view + manage
 *   • can_view_crane_calendar:   can see the calendar
 *   • can_manage_crane_calendar: can create, edit, delete reservations
 */
export default function Crane() {
  const { user } = useAuth();
  const canView = user?.role === "sadmin" || user?.can_view_crane_calendar || user?.can_manage_crane_calendar;
  const canManage = user?.role === "sadmin" || user?.can_manage_crane_calendar;

  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [query, setQuery] = useState("");

  const monthStart = useMemo(() => toLocalYmd(new Date(cursor.year, cursor.month, 1)), [cursor]);
  const monthEnd = useMemo(() => {
    const cells = buildMonthGrid(cursor.year, cursor.month);
    return cells[cells.length - 1].date;
  }, [cursor]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const { data } = await api.get("/crane/reservations", {
        params: { fecha_desde: monthStart, fecha_hasta: monthEnd, limit: 500 },
      });
      setReservations(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el calendario");
    } finally {
      setLoading(false);
    }
  }, [canView, monthStart, monthEnd]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const monthLabel = useMemo(() => {
    const d = new Date(cursor.year, cursor.month, 1);
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [cursor]);

  const visible = useMemo(
    () => reservations.filter((r) =>
      matchesQuery(query, r.motivo, r.obra, r.operador, r.created_by_name, r.notas)
    ),
    [reservations, query]
  );
  const byDate = useMemo(() => {
    const map = {};
    for (const r of visible) {
      _expandCraneDates(r).forEach((d) => {
        (map[d] = map[d] || []).push(r);
      });
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
    return map;
  }, [visible]);

  const prevMonth = () => setCursor((c) => {
    const d = new Date(c.year, c.month - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const nextMonth = () => setCursor((c) => {
    const d = new Date(c.year, c.month + 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const goToday = () => {
    const d = new Date();
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };
  const openCreate = (dateHint) => {
    setPrefillDate(dateHint);
    setCreating(true);
  };

  if (!canView) {
    return (
      <div className="brutal-card p-8 text-center" data-testid="crane-no-access">
        <ShieldAlert size={40} className="mx-auto text-slate-400 mb-3" />
        <h1 className="font-display font-black text-2xl">Calendario Grúa</h1>
        <p className="text-sm text-slate-500 mt-2">
          No tienes acceso a este calendario. Pide al super-administrador que te
          otorgue permiso desde el panel de usuarios.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="crane-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="label-up text-[#F5A623]">Reservas</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Calendario Grúa</h1>
          <p className="text-zinc-600 text-sm mt-1">
            {canManage
              ? "Reserva la grúa para tus trabajos. Pincha en un día para añadir una reserva."
              : "Consulta cuándo está reservada la grúa. Solo los responsables pueden modificar."}
          </p>
        </div>
        {canManage && (
          <button
            data-testid="crane-create-btn"
            onClick={() => openCreate(todayLocalYmd())}
            className="brutal-btn-primary h-11 px-4 text-sm inline-flex items-center gap-2 shrink-0"
          >
            <Plus size={16} /> Reservar
          </button>
        )}
      </header>

      <LiveSearch
        value={query} onChange={setQuery}
        placeholder="Buscar por motivo, obra, operador…"
        testId="crane-search"
      />

      <div className="brutal-card p-3 flex items-center justify-center gap-1">
        <button onClick={prevMonth} data-testid="crane-prev-month"
          className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center">
          <ChevronLeft size={16} />
        </button>
        <button onClick={goToday} data-testid="crane-today"
          className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 min-w-[160px] capitalize">
          {monthLabel}
        </button>
        <button onClick={nextMonth} data-testid="crane-next-month"
          className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center">
          <ChevronRight size={16} />
        </button>
      </div>

      {loading && (
        <div className="brutal-card p-6 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {/* Google-style month grid */}
      <div className="brutal-card p-0 overflow-hidden" data-testid="crane-calendar">
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d) => (
            <div key={d} className="text-[10px] uppercase font-bold tracking-wider text-slate-500 text-center py-2">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-[minmax(96px,auto)]">
          {cells.map((c) => {
            const day = byDate[c.date] || [];
            const clickable = c.inMonth && canManage;
            return (
              <div
                key={c.date}
                onClick={() => clickable && day.length === 0 && openCreate(c.date)}
                className={`relative border-b border-r border-slate-100 p-1.5 flex flex-col gap-1 min-h-[96px] ${
                  c.inMonth ? "bg-white" : "bg-slate-50/50"
                } ${clickable ? "cursor-pointer hover:bg-amber-50/50" : ""}`}
                data-testid={`crane-cell-${c.date}`}
              >
                <div className={`text-[10px] font-semibold self-start ${
                  c.isToday
                    ? "bg-[#F5A623] text-white rounded-full w-5 h-5 inline-flex items-center justify-center"
                    : c.inMonth ? "text-slate-700" : "text-slate-300"
                }`}>
                  {c.dayOfMonth}
                </div>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {day.slice(0, 3).map((r) => {
                    const isRange = r.fecha_fin && r.fecha_fin !== r.fecha;
                    const isRecurring = Array.isArray(r.recurrencia_dias) && r.recurrencia_dias.length > 0;
                    const prefix = isRecurring ? "↻ " : r.todo_el_dia ? "◐ " : `${r.hora_inicio} `;
                    return (
                      <button
                        key={r.id + "-" + c.date}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelected(r); }}
                        data-testid={`crane-event-${r.id}`}
                        title={`${r.hora_inicio}–${r.hora_fin} · ${r.motivo}${isRecurring ? " (recurrente)" : ""}`}
                        className={`text-left text-[10px] leading-tight rounded px-1 py-0.5 truncate inline-flex items-center gap-1 border bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200 ${
                          isRange ? "border-l-2 border-l-[#F5A623]" : ""
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623] shrink-0" />
                        <span className="truncate font-semibold">{prefix}{r.motivo}</span>
                      </button>
                    );
                  })}
                  {day.length > 3 && (
                    <span className="text-[10px] text-slate-500 pl-1">+{day.length - 3} más</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <ReservationDetailModal
          reservation={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setSelected(null); }}
          onDeleted={() => { setSelected(null); load(); }}
        />
      )}

      {(creating || editing) && (
        <ReservationFormModal
          reservation={editing}
          prefillDate={prefillDate}
          onClose={() => { setCreating(false); setEditing(null); setPrefillDate(null); }}
          onSaved={() => { setCreating(false); setEditing(null); setPrefillDate(null); load(); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
function ReservationDetailModal({ reservation, canManage, onClose, onEdit, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(`¿Cancelar la reserva "${reservation.motivo}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/crane/reservations/${reservation.id}`);
      toast.success("Reserva cancelada");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="crane-detail-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="label-up text-[#F5A623]">Reserva grúa</div>
          <button onClick={onClose} className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <h3 className="font-display font-black text-2xl text-[#0B2A4A] leading-tight">{reservation.motivo}</h3>
          {(reservation.fecha_fin && reservation.fecha_fin !== reservation.fecha) || (reservation.recurrencia_dias?.length) ? (
            <div className="flex flex-wrap gap-1.5">
              {reservation.fecha_fin && reservation.fecha_fin !== reservation.fecha && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-300">
                  Multi-día
                </span>
              )}
              {reservation.recurrencia_dias?.length > 0 && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border bg-amber-100 text-amber-900 border-amber-400">
                  Recurrente · {reservation.recurrencia_dias.map((d) => WEEKDAYS[d]).join(" · ")}
                </span>
              )}
              {reservation.todo_el_dia && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-300">
                  Día completo
                </span>
              )}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow
              icon={<Clock size={14} />}
              label={reservation.fecha_fin && reservation.fecha_fin !== reservation.fecha ? "Del" : "Fecha"}
              value={
                reservation.fecha_fin && reservation.fecha_fin !== reservation.fecha
                  ? `${formatFechaCorta(reservation.fecha)} al ${formatFechaCorta(reservation.fecha_fin)}`
                  : formatFechaCorta(reservation.fecha)
              }
            />
            <InfoRow icon={<Clock size={14} />} label="Horario" value={`${reservation.hora_inicio}–${reservation.hora_fin}`} />
            {reservation.obra && <InfoRow icon={<MapPin size={14} />} label="Obra" value={reservation.obra} />}
            {reservation.operador && <InfoRow icon={<User size={14} />} label="Operador" value={reservation.operador} />}
          </div>
          {reservation.notas && (
            <div>
              <div className="label-up mb-1">Notas</div>
              <p className="text-sm whitespace-pre-wrap text-slate-700">{reservation.notas}</p>
            </div>
          )}
          <div className="text-[10px] text-slate-400 pt-1 border-t border-dashed border-slate-200">
            Reservada por {reservation.created_by_name || "—"}
          </div>
        </div>
        {canManage && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-2">
            <button type="button" onClick={onEdit} disabled={busy}
              data-testid="crane-edit"
              className="flex-1 h-10 rounded-lg border border-slate-200 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 inline-flex items-center justify-center gap-1.5">
              <Edit3 size={12} /> Editar
            </button>
            <button type="button" onClick={remove} disabled={busy}
              data-testid="crane-delete"
              className="flex-1 h-10 rounded-lg border border-rose-300 text-rose-700 text-xs font-bold uppercase tracking-wider hover:bg-rose-50 inline-flex items-center justify-center gap-1.5">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-slate-400 mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        <div className="label-up text-[9px]">{label}</div>
        <div className="text-slate-800 font-semibold">{value || "—"}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function ReservationFormModal({ reservation, prefillDate, onClose, onSaved }) {
  const isEdit = !!reservation;
  const [form, setForm] = useState(() => reservation || {
    fecha: prefillDate || todayLocalYmd(),
    fecha_fin: "",
    todo_el_dia: false,
    recurrencia_dias: [],
    hora_inicio: "09:00",
    hora_fin: "13:00",
    motivo: "",
    obra: "",
    operador: "",
    notas: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleWeekday = (d) => setForm((f) => ({
    ...f,
    recurrencia_dias: f.recurrencia_dias.includes(d)
      ? f.recurrencia_dias.filter((x) => x !== d)
      : [...f.recurrencia_dias, d].sort((a, b) => a - b),
  }));

  const isRange = form.fecha_fin && form.fecha_fin !== form.fecha;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.motivo.trim()) { toast.error("El motivo es obligatorio"); return; }
    if (form.hora_fin <= form.hora_inicio) { toast.error("La hora fin debe ser posterior a la hora inicio"); return; }
    if (form.fecha_fin && form.fecha_fin < form.fecha) {
      toast.error("La fecha fin no puede ser anterior a la fecha inicio"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        fecha_fin: form.fecha_fin || null,
        recurrencia_dias: form.recurrencia_dias || [],
      };
      if (isEdit) {
        await api.put(`/crane/reservations/${reservation.id}`, payload);
        toast.success("Reserva actualizada");
      } else {
        await api.post("/crane/reservations", payload);
        toast.success("Grúa reservada");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="crane-form-modal">
      <form onSubmit={submit}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-black text-lg text-[#0B2A4A]">
            {isEdit ? "Editar reserva" : "Nueva reserva de grúa"}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label-up block mb-1">Motivo *</label>
            <input className="brutal-input w-full" required data-testid="crane-motivo"
              value={form.motivo} onChange={(e) => update("motivo", e.target.value)}
              placeholder="Ej: elevar cuadro AC" />
          </div>
          <div>
            <label className="label-up block mb-1">Obra</label>
            <input className="brutal-input w-full" data-testid="crane-obra"
              value={form.obra} onChange={(e) => update("obra", e.target.value)}
              placeholder="Nombre / dirección" />
          </div>
          <div>
            <label className="label-up block mb-1">Operador</label>
            <input className="brutal-input w-full" data-testid="crane-operador"
              value={form.operador} onChange={(e) => update("operador", e.target.value)}
              placeholder="Quién conduce la grúa" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-up block mb-1">Fecha inicio *</label>
              <input type="date" className="brutal-input w-full" required data-testid="crane-fecha"
                value={form.fecha} onChange={(e) => update("fecha", e.target.value)} />
            </div>
            <div>
              <label className="label-up block mb-1">Fecha fin</label>
              <input type="date" className="brutal-input w-full" data-testid="crane-fecha-fin"
                min={form.fecha || undefined}
                value={form.fecha_fin || ""}
                onChange={(e) => update("fecha_fin", e.target.value)} />
            </div>
          </div>

          {isRange && (
            <div>
              <label className="label-up block mb-1">Repetir en días (recurrencia)</label>
              <div className="flex gap-1.5 flex-wrap">
                {WEEKDAYS.map((label, i) => {
                  const on = form.recurrencia_dias.includes(i);
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => toggleWeekday(i)}
                      data-testid={`crane-weekday-${i}`}
                      className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${
                        on
                          ? "bg-[#F5A623] text-white shadow-md"
                          : "bg-slate-100 text-slate-500 hover:bg-amber-100"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {form.recurrencia_dias.length === 0
                  ? "Vacío = todos los días entre las dos fechas."
                  : `Solo estos días de la semana dentro del rango: ${form.recurrencia_dias.map((d) => WEEKDAYS[d]).join(" · ")}.`}
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 select-none cursor-pointer" data-testid="crane-todo-el-dia-wrapper">
            <input
              type="checkbox"
              data-testid="crane-todo-el-dia"
              className="w-5 h-5 brutal-border accent-[#F5A623]"
              checked={!!form.todo_el_dia}
              onChange={(e) => update("todo_el_dia", e.target.checked)}
            />
            <span className="text-sm font-bold uppercase tracking-wider text-slate-700">
              Día completo (sin horario específico)
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-up block mb-1">Hora inicio *</label>
              <input type="time" className="brutal-input w-full" required data-testid="crane-hora-inicio"
                value={form.hora_inicio} onChange={(e) => update("hora_inicio", e.target.value)} />
            </div>
            <div>
              <label className="label-up block mb-1">Hora fin *</label>
              <input type="time" className="brutal-input w-full" required data-testid="crane-hora-fin"
                value={form.hora_fin} onChange={(e) => update("hora_fin", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label-up block mb-1">Notas</label>
            <textarea className="brutal-input w-full min-h-[60px]" data-testid="crane-notas"
              value={form.notas} onChange={(e) => update("notas", e.target.value)} />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold">
            Cancelar
          </button>
          <button type="submit" disabled={saving}
            data-testid="crane-form-submit"
            className="flex-1 h-11 rounded-lg bg-[#FF4500] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {isEdit ? "Guardar" : "Reservar"}
          </button>
        </div>
      </form>
    </div>
  );
}
